import { clamp } from './math';
import { valueNoise } from './noise';
import { Surface } from './terrain';

/** Steep faces of doubles and step-ups, metres along the track. */
export const FACE = 1.2;
const ROOT_SEED = 7331;

export type FeatureDef =
  | { kind: 'berm'; s: number; length: number; height: number; lip?: number }
  | { kind: 'whoops'; s: number; length: number; height: number; spacing: number }
  | { kind: 'tabletop'; s: number; up: number; top: number; down: number; height: number; refSpeed: number; kick?: number }
  | { kind: 'double'; s: number; up: number; gap: number; down: number; height: number; refSpeed: number }
  | { kind: 'roller'; s: number; length: number; height: number; refSpeed: number }
  | { kind: 'stepup'; s: number; up: number; gap: number; height: number; landHeight: number; top: number; down: number; refSpeed: number }
  | { kind: 'log'; s: number; length: number; height: number }
  | { kind: 'roots'; s: number; length: number; height: number }
  | { kind: 'ford'; s: number; length: number; depth: number };

/** refSpeed: speed (m/s) held on the approach, from 30 m before the take-off ramp, that clears the jump. */
export type JumpDef = Extract<FeatureDef, { refSpeed: number }>;
export const isJump = (f: FeatureDef): f is JumpDef => 'refSpeed' in f;

export function featureLength(f: FeatureDef): number {
  switch (f.kind) {
    case 'tabletop': return f.up + f.top + f.down;
    case 'double': return f.up + f.gap + f.down;
    case 'stepup': return f.up + f.gap + f.top + f.down;
    default: return f.length;
  }
}

/** Arc length from the feature start to the lip and to the first point a clean landing reaches. */
export function jumpSpan(f: JumpDef): { lip: number; land: number } {
  if (f.kind === 'tabletop') return { lip: f.up, land: f.up + f.top };
  // A roller's crest is both: ridden over it the bike gets light just past the crest.
  if (f.kind === 'roller') return { lip: f.length / 2, land: f.length / 2 };
  return { lip: f.up, land: f.up + f.gap };
}

export function featureSurface(f: FeatureDef): Surface | undefined {
  if (f.kind === 'roots') return 'roots';
  if (f.kind === 'ford') return 'water';
  return undefined;
}

const up = (u: number, len: number, h: number) => h * clamp(u / len, 0, 1);
const down = (u: number, len: number, h: number) => h * clamp(1 - u / len, 0, 1);

/**
 * Height added at `u` metres into the feature and `d` metres left of the centreline.
 * `outer` is +1 when the outside of the turn is on the left (d > 0).
 */
export function featureHeight(f: FeatureDef, u: number, d: number, halfWidth: number, outer: number): number {
  if (u < 0 || u > featureLength(f)) return 0;
  switch (f.kind) {
    case 'berm': {
      // Optional lip: a ridge of pushed-out dirt on top of the berm's outer wall.
      const x = (d * outer) / halfWidth, along = Math.sin((Math.PI * u) / f.length);
      const lip = f.lip ? f.lip * Math.exp(-(((x - 1.3) / 0.15) ** 2)) : 0;
      return along * (f.height * clamp(x, 0, 1.5) + lip);
    }
    case 'whoops': {
      const n = Math.floor(f.length / f.spacing);
      return u > n * f.spacing ? 0 : f.height * Math.sin((Math.PI * u) / f.spacing) ** 2;
    }
    case 'tabletop':
      // kick (0-1) bends the face so it steepens into the lip: a sharp lip instead of a straight ramp.
      if (u < f.up) { const r = u / f.up; return f.height * ((1 - (f.kick ?? 0)) * r + (f.kick ?? 0) * r * r * r); }
      if (u < f.up + f.top) return f.height;
      return down(u - f.up - f.top, f.down, f.height);
    case 'double':
      if (u < f.up) return up(u, f.up, f.height);
      if (u < f.up + f.gap - FACE) return down(u - f.up, FACE, f.height);
      if (u < f.up + f.gap) return up(u - (f.up + f.gap - FACE), FACE, f.height);
      return down(u - f.up - f.gap, f.down, f.height);
    case 'roller':
      // A smooth hump: one cosine period, no edges.
      return f.height * 0.5 * (1 - Math.cos((2 * Math.PI * u) / f.length));
    case 'stepup':
      if (u < f.up) return up(u, f.up, f.height);
      if (u < f.up + f.gap - FACE) return down(u - f.up, FACE, f.height);
      if (u < f.up + f.gap) return up(u - (f.up + f.gap - FACE), FACE, f.landHeight);
      if (u < f.up + f.gap + f.top) return f.landHeight;
      return down(u - f.up - f.gap - f.top, f.down, f.landHeight);
    case 'log': {
      const x = (2 * u) / f.length - 1;
      return f.height * Math.sqrt(Math.max(0, 1 - x * x));
    }
    case 'roots':
      return f.height * Math.max(0, valueNoise(ROOT_SEED, u * 2, d * 2));
    case 'ford':
      return -f.depth * Math.sin((Math.PI * u) / f.length);
  }
}
